/**
 * cartographyPdf.js — Génération d'un PDF multi-pages « Cartographie 360° »
 * brandé Noam, prêt à envoyer à un client / comité / famille d'investissement.
 *
 * Structure :
 *   1. Couverture (titre du sujet + « Préparé pour » + date + résumé)
 *   2. Résumé exécutif + doctrine de référence
 *   3. Position fractale verticale (5 niveaux Micro → Macro)
 *   4. Relations horizontales (annuaire, chains, utility)
 *   5. Frictions & bottlenecks
 *   6. Plans d'action
 *   7. Fantômes détectés + coûts d'inaction
 *   8. Annexe (méthodologie + métadonnées)
 *
 * Chaque page porte l'en-tête Noam + numéro de page + fil rouge du sujet.
 * Aucun appel réseau — pure transformation des données déjà chargées.
 *
 * Dépendance : jspdf (déjà installé pour PitchCaptureButton).
 */
import { jsPDF } from "jspdf";

const W = 210; // A4 largeur mm
const H = 297; // A4 hauteur mm
const M = 16;  // marge

const COLORS = {
  ink:      [20, 20, 28],
  soft:     [95, 95, 108],
  mute:     [140, 140, 150],
  line:     [220, 220, 226],
  gold:     [239, 159, 39],
  blue:     [55, 138, 221],
  green:    [29, 158, 117],
  red:      [226, 75, 74],
  violet:   [127, 119, 221],
  dark:     [10, 10, 15],
};

function slugify(s) {
  return (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);
}

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return ""; }
}

function fmtDateTime(iso) {
  try { return new Date(iso).toLocaleString("fr-CH", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

// -----------------------------------------------------------------------------
// Éléments d'en-tête / pied de page — appelés à chaque nouvelle page
// -----------------------------------------------------------------------------

function drawHeader(doc, ctx) {
  // Barre de titre fine, non intrusive
  doc.setDrawColor(...COLORS.line);
  doc.setLineWidth(0.2);
  doc.line(M, 12, W - M, 12);

  // Noam à gauche
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.ink);
  doc.text("NOAM", M, 9);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...COLORS.mute);
  doc.text("· Constante Partners", M + 12, 9);

  // Sujet à droite (fil rouge)
  const subject = doc.splitTextToSize(ctx.subjectLabel || "", 110)[0] || "";
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...COLORS.soft);
  doc.text(subject, W - M, 9, { align: "right" });
}

function drawFooter(doc, ctx, pageNum, total) {
  doc.setDrawColor(...COLORS.line);
  doc.setLineWidth(0.2);
  doc.line(M, H - 14, W - M, H - 14);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...COLORS.mute);
  doc.text("Constante Partners · Genève · contact@constante-partners.com", M, H - 9);
  doc.setTextColor(...COLORS.soft);
  doc.text(`Page ${pageNum} / ${total}`, W - M, H - 9, { align: "right" });
  if (ctx.clientName) {
    doc.setTextColor(...COLORS.gold);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(6.5);
    doc.text(`PRÉPARÉ POUR ${ctx.clientName.toUpperCase()}`, W / 2, H - 9, { align: "center" });
  }
}

function pageHeading(doc, y, label, color = COLORS.ink) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  doc.setTextColor(...COLORS.gold);
  doc.text("SECTION", M, y - 3);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(...color);
  doc.text(label, M, y + 4);
  doc.setDrawColor(...COLORS.gold);
  doc.setLineWidth(0.6);
  doc.line(M, y + 6.5, M + 24, y + 6.5);
  return y + 14;
}

function bodyText(doc, y, text, opts = {}) {
  const {
    size = 9.5,
    color = COLORS.ink,
    maxWidth = W - 2 * M,
    x = M,
    bold = false,
  } = opts;
  doc.setFont("helvetica", bold ? "bold" : "normal");
  doc.setFontSize(size);
  doc.setTextColor(...color);
  const lines = doc.splitTextToSize(String(text || ""), maxWidth);
  doc.text(lines, x, y);
  return y + lines.length * (size * 0.42);
}

function ensureSpace(doc, ctx, y, needed) {
  if (y + needed > H - 20) {
    return newPage(doc, ctx);
  }
  return y;
}

function newPage(doc, ctx) {
  doc.addPage();
  ctx.pageCount++;
  drawHeader(doc, ctx);
  return 26; // y initial après header
}

// -----------------------------------------------------------------------------
// Page 1 — Couverture
// -----------------------------------------------------------------------------

function drawCoverPage(doc, ctx, data) {
  // Fond sombre en haut
  doc.setFillColor(...COLORS.dark);
  doc.rect(0, 0, W, 90, "F");

  // Noam brand
  doc.setFont("helvetica", "bold");
  doc.setFontSize(36);
  doc.setTextColor(255, 255, 255);
  doc.text("NOAM", M, 34);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(160, 160, 170);
  doc.text("by Constante Partners", M, 42);

  // Filet or
  doc.setDrawColor(...COLORS.gold);
  doc.setLineWidth(0.8);
  doc.line(M, 50, M + 28, 50);

  // Type de document
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.gold);
  doc.text("CARTOGRAPHIE 360° · DIAGNOSTIC DE TRAJECTOIRE", M, 60);

  // Titre du sujet
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(255, 255, 255);
  const subjectTitle = doc.splitTextToSize(data.subject?.label || "Sujet", W - 2 * M);
  doc.text(subjectTitle.slice(0, 3), M, 72);

  // Métadonnées sujet en cover
  const subMeta = [
    data.subject?.secteur && `Secteur : ${data.subject.secteur}`,
    data.subject?.country && `Pays : ${data.subject.country}`,
    data.subject?.status && `Statut : ${data.subject.status}`,
    data.subject?.owner && `Owner : ${data.subject.owner}`,
  ].filter(Boolean).join("   ·   ");
  if (subMeta) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(200, 200, 210);
    doc.text(doc.splitTextToSize(subMeta, W - 2 * M), M, 86);
  }

  // Bloc « Préparé pour » (si nominatif)
  let y = 108;
  if (ctx.clientName) {
    doc.setFillColor(...COLORS.gold);
    doc.rect(M, y - 5, 1.4, 12, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.gold);
    doc.text("PRÉPARÉ POUR", M + 4, y);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.setTextColor(...COLORS.ink);
    doc.text(doc.splitTextToSize(ctx.clientName, W - 2 * M - 4)[0], M + 4, y + 6);
    y += 20;
  }

  // Résumé exécutif court (description sujet + doctrine)
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.ink);
  doc.text("Synthèse", M, y);
  y += 5;
  const synth = data.subject?.description
    || `Vue x-ray multi-couches sur « ${data.subject?.label || "ce sujet"} » : position fractale, relations, frictions, plans d'action et fantômes détectés.`;
  y = bodyText(doc, y, synth, { size: 9.5, color: COLORS.soft, maxWidth: W - 2 * M });
  y += 4;

  if (data.doctrine) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.violet);
    doc.text("Doctrine de référence", M, y);
    y += 5;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.ink);
    doc.text(`${data.doctrine.title} · v${data.doctrine.version} · ${data.doctrine.principles_count} principes`, M, y);
    y += 8;
  }

  // Date en bas
  const dateStr = fmtDate(new Date().toISOString());
  doc.setDrawColor(...COLORS.line);
  doc.setLineWidth(0.3);
  doc.line(M, H - 30, W - M, H - 30);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...COLORS.mute);
  doc.text(`Édité le ${dateStr}`, M, H - 24);
  doc.text("Document confidentiel · Ne pas diffuser sans accord de l'émetteur.", M, H - 20);
  doc.setTextColor(...COLORS.gold);
  doc.setFont("helvetica", "bold");
  doc.text("noam.constante-partners.com", W - M, H - 20, { align: "right" });
}

// -----------------------------------------------------------------------------
// Page 2 — Résumé exécutif structuré
// -----------------------------------------------------------------------------

function drawExecutivePage(doc, ctx, data) {
  let y = newPage(doc, ctx);
  y = pageHeading(doc, y, "Résumé exécutif");

  const s = data.subject || {};
  const rows = [
    s.secteur && ["Secteur", s.secteur],
    s.ticket_mEUR != null && ["Ticket", `${s.ticket_mEUR} M€`],
    s.etape && ["Étape", s.etape],
    s.owner && ["Owner", s.owner],
    s.country && ["Pays", s.country],
    s.status && ["Statut", s.status],
    s.niveau && ["Niveau", s.niveau],
    s.impact_net != null && ["Impact net", s.impact_net],
    s.probabilite_nette != null && ["Probabilité nette", s.probabilite_nette],
    s.cout_inaction != null && ["Coût d'inaction", `${s.cout_inaction} k€`],
    s.prob_closing != null && ["Prob. closing", `${s.prob_closing}%`],
  ].filter(Boolean);

  if (rows.length > 0) {
    for (const [k, v] of rows) {
      y = ensureSpace(doc, ctx, y, 6);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(...COLORS.mute);
      doc.text(String(k).toUpperCase(), M, y);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(...COLORS.ink);
      doc.text(String(v), M + 42, y);
      y += 5.5;
    }
    y += 4;
  }

  if (s.description) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    doc.setTextColor(...COLORS.blue);
    doc.text("Description", M, y);
    y += 5;
    y = bodyText(doc, y, s.description, { size: 9.5, color: COLORS.ink });
    y += 4;
  }

  if (data.doctrine) {
    y = ensureSpace(doc, ctx, y, 20);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    doc.setTextColor(...COLORS.violet);
    doc.text("Doctrine active de référence", M, y);
    y += 5;
    y = bodyText(doc, y, `${data.doctrine.title} · version ${data.doctrine.version} · ${data.doctrine.principles_count} principes actifs.`, { size: 9.5, color: COLORS.ink });
  }
}

// -----------------------------------------------------------------------------
// Page 3 — Position fractale verticale
// -----------------------------------------------------------------------------

function drawFractalPage(doc, ctx, data) {
  const fractal = data.fractal_position;
  if (!fractal) return;

  let y = newPage(doc, ctx);
  y = pageHeading(doc, y, "Position fractale");

  const complete = fractal.completeness_pct != null ? ` · Complétude ${fractal.completeness_pct}%` : "";
  y = bodyText(doc, y, `Position verticale du sujet dans les 5 niveaux causals (du plus concret au plus systémique). Utilisé pour identifier à quel étage la valeur est capturée et où l'effet de levier est le plus fort.${complete}`, { size: 9, color: COLORS.soft });
  y += 4;

  const levels = fractal.levels || [];
  const palette = [COLORS.blue, COLORS.green, COLORS.violet, COLORS.gold, COLORS.red];

  for (const lv of levels) {
    y = ensureSpace(doc, ctx, y, 24);
    const idx = Math.max(0, Math.min(palette.length - 1, (lv.n || 1) - 1));
    const color = palette[idx];

    doc.setFillColor(...color);
    doc.rect(M, y - 3, 2.4, 16, "F");
    doc.setDrawColor(...COLORS.line);
    doc.setLineWidth(0.3);
    doc.rect(M + 4, y - 3, W - 2 * M - 4, 18);
    if (lv.filled) {
      doc.setDrawColor(...COLORS.gold);
      doc.setLineWidth(0.6);
      doc.rect(M + 4, y - 3, W - 2 * M - 4, 18);
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...color);
    doc.text(String(lv.title || lv.key || `N${lv.n}`).toUpperCase(), M + 8, y + 2);
    if (!lv.filled) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(...COLORS.mute);
      doc.text("· niveau non renseigné", M + 40, y + 2);
    }

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.ink);
    const desc = doc.splitTextToSize(lv.value || "—", W - 2 * M - 12);
    doc.text(desc.slice(0, 2), M + 8, y + 8);
    y += 22;
  }
}

// -----------------------------------------------------------------------------
// Page 4 — Relations horizontales
// -----------------------------------------------------------------------------

function drawRelationsPage(doc, ctx, data) {
  const rels = data.relations;
  if (!rels) return;

  let y = newPage(doc, ctx);
  y = pageHeading(doc, y, "Relations horizontales");

  y = bodyText(doc, y, "Réseau d'entités, d'acteurs pivots et de chaînes de valeur connectés à ce sujet. Base de la stratégie relationnelle.", { size: 9, color: COLORS.soft });
  y += 4;

  const groups = [
    { key: "annuaire", label: "Entités liées (annuaire)", color: COLORS.blue },
    { key: "utility_actors", label: "Acteurs pivots (utility mapping)", color: COLORS.green },
    { key: "chains", label: "Chaînes de valeur", color: COLORS.gold },
  ];

  let hasContent = false;
  for (const g of groups) {
    const items = rels[g.key] || [];
    if (items.length === 0) continue;
    hasContent = true;
    y = ensureSpace(doc, ctx, y, 12);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...g.color);
    doc.text(`${g.label} · ${items.length}`, M, y);
    y += 5;

    for (const it of items.slice(0, 12)) {
      y = ensureSpace(doc, ctx, y, 8);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(...COLORS.ink);
      const label = it.label || it.name || it.title || "—";
      const meta = [it.role, it.sector, it.country].filter(Boolean).join(" · ");
      doc.text(`•  ${label}`, M + 2, y);
      if (meta) {
        doc.setFontSize(7.5);
        doc.setTextColor(...COLORS.mute);
        doc.text(meta, M + 6, y + 3);
      }
      y += meta ? 7 : 5;
    }
    if (items.length > 12) {
      doc.setFontSize(7.5);
      doc.setTextColor(...COLORS.mute);
      doc.text(`+ ${items.length - 12} autres`, M + 6, y);
      y += 5;
    }
    y += 3;
  }

  if (!hasContent) {
    y = bodyText(doc, y, "Aucune relation cartographiée pour ce sujet — angle mort relationnel.", { size: 9, color: COLORS.mute });
  }
}

// -----------------------------------------------------------------------------
// Page 5 — Frictions
// -----------------------------------------------------------------------------

function drawFrictionsPage(doc, ctx, data) {
  const fricRoot = data.frictions;
  const frictions = Array.isArray(fricRoot) ? fricRoot : (fricRoot?.items || []);
  const totalCost = !Array.isArray(fricRoot) ? fricRoot?.total_cost_monthly_meur : null;

  let y = newPage(doc, ctx);
  y = pageHeading(doc, y, "Frictions & bottlenecks");

  y = bodyText(doc, y, "Points de blocage identifiés sur la trajectoire de ce sujet — chaque friction non traitée devient un coût récurrent.", { size: 9, color: COLORS.soft });
  y += 4;

  if (totalCost > 0) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.red);
    doc.text(`Coût cumulé estimé : ${Number(totalCost).toFixed(2)} M€/mois`, M, y);
    y += 6;
  }

  if (frictions.length === 0) {
    y = bodyText(doc, y, "Aucune friction enregistrée pour ce sujet.", { size: 9, color: COLORS.mute });
    return;
  }

  for (const f of frictions) {
    y = ensureSpace(doc, ctx, y, 18);
    // Filet rouge
    doc.setFillColor(...COLORS.red);
    doc.rect(M, y - 3, 1.4, 10, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.ink);
    const title = f.label || f.title || "Friction";
    const titleLines = doc.splitTextToSize(title, W - 2 * M - 6);
    doc.text(titleLines.slice(0, 2), M + 4, y + 1);
    y += titleLines.length > 1 ? 9 : 5;

    const meta = [
      f.categorie && `Catégorie : ${f.categorie}`,
      f.score_friction != null && `Score : ${f.score_friction}/10`,
      f.cost_inaction_meur_monthly != null && `Coût : ${Number(f.cost_inaction_meur_monthly).toFixed(2)} M€/mois`,
      f.owner && `Owner : ${f.owner}`,
    ].filter(Boolean).join("  ·  ");
    if (meta) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(...COLORS.mute);
      doc.text(meta, M + 4, y);
      y += 4;
    }
    const lever = f.comment_lever || f.description;
    if (lever) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(...COLORS.green);
      doc.text("Levier :", M + 4, y);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...COLORS.ink);
      const lines = doc.splitTextToSize(lever, W - 2 * M - 20);
      doc.text(lines, M + 20, y);
      y += lines.length * 3.6 + 1.5;
    }
    y += 3;
  }
}

// -----------------------------------------------------------------------------
// Page 6 — Plans d'action
// -----------------------------------------------------------------------------

function drawPlansPage(doc, ctx, data) {
  const plansRoot = data.plans;
  const plans = Array.isArray(plansRoot) ? plansRoot : (plansRoot?.items || []);
  const openCount = !Array.isArray(plansRoot) ? plansRoot?.open_count : null;
  const lateCount = !Array.isArray(plansRoot) ? plansRoot?.late_count : null;

  let y = newPage(doc, ctx);
  y = pageHeading(doc, y, "Plans d'action");

  y = bodyText(doc, y, "Actions engagées ou proposées, avec propriétaire, échéance et statut d'avancement.", { size: 9, color: COLORS.soft });
  y += 4;

  if (openCount != null || lateCount != null) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.ink);
    const bits = [];
    if (openCount != null) bits.push(`Ouverts : ${openCount}`);
    if (lateCount != null) bits.push(`En retard : ${lateCount}`);
    doc.text(bits.join("  ·  "), M, y);
    y += 6;
  }

  if (plans.length === 0) {
    y = bodyText(doc, y, "Aucun plan d'action associé à ce sujet.", { size: 9, color: COLORS.mute });
    return;
  }

  for (const p of plans) {
    y = ensureSpace(doc, ctx, y, 14);
    doc.setFillColor(...COLORS.green);
    doc.rect(M, y - 3, 1.4, 10, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.ink);
    doc.text(doc.splitTextToSize(p.title || p.label || "Plan", W - 2 * M - 6), M + 4, y + 1);
    y += 5;

    const meta = [
      p.owner && `Owner : ${p.owner}`,
      p.due_date && `Échéance : ${fmtDate(p.due_date)}`,
      p.status && `Statut : ${p.status}`,
      p.progress != null && `Avancement : ${p.progress}%`,
    ].filter(Boolean).join("  ·  ");
    if (meta) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(...COLORS.mute);
      doc.text(meta, M + 4, y);
      y += 4;
    }
    if (p.description) {
      y = bodyText(doc, y, p.description, { size: 9, color: COLORS.ink, x: M + 4, maxWidth: W - 2 * M - 6 });
    }
    y += 4;
  }
}

// -----------------------------------------------------------------------------
// Page 7 — Fantômes détectés
// -----------------------------------------------------------------------------

function drawPhantomsPage(doc, ctx, data) {
  const scan = data.phantom_scan;
  if (!scan) return;

  let y = newPage(doc, ctx);
  y = pageHeading(doc, y, "Fantômes détectés");

  y = bodyText(doc, y, "Ce qui devrait apparaître dans la cartographie et n'y figure pas — l'angle mort du dossier. Chaque fantôme est un risque non chiffré aujourd'hui, un coût demain.", { size: 9, color: COLORS.soft });
  y += 4;

  // Score
  y = ensureSpace(doc, ctx, y, 30);
  doc.setDrawColor(...COLORS.violet);
  doc.setLineWidth(0.4);
  doc.rect(M, y, 40, 22);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(28);
  doc.setTextColor(...COLORS.violet);
  doc.text(String(scan.phantom_score ?? "—"), M + 20, y + 15, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...COLORS.mute);
  doc.text("PHANTOM SCORE · 0-100", M + 20, y + 20, { align: "center" });

  // Synthèse
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.ink);
  doc.text("Synthèse", M + 46, y + 5);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  const synth = doc.splitTextToSize(scan.synthese || "—", W - M - 46 - M);
  doc.text(synth.slice(0, 5), M + 46, y + 10);

  y += 26;
  if (scan.total_cost_monthly_meur > 0) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.red);
    doc.text(`Coût d'inaction cumulé : ${Number(scan.total_cost_monthly_meur).toFixed(2)} M€/mois`, M, y);
    y += 6;
  }

  y += 2;
  const phantoms = scan.phantoms || [];
  for (const p of phantoms) {
    y = ensureSpace(doc, ctx, y, 20);
    const sevColor = { critique: COLORS.red, majeur: COLORS.gold, mineur: [244, 211, 94], info: COLORS.violet }[p.severity] || COLORS.mute;
    doc.setFillColor(...sevColor);
    doc.rect(M, y - 3, 1.4, 14, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    doc.setTextColor(...COLORS.ink);
    doc.text(doc.splitTextToSize(p.label || "Fantôme", W - 2 * M - 6), M + 4, y + 1);
    y += 5;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(...sevColor);
    doc.text(`${(p.severity || "").toUpperCase()}${p.type_label ? "  ·  " + p.type_label.toUpperCase() : ""}`, M + 4, y);
    y += 4;

    if (p.expected_what) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(...COLORS.soft);
      doc.text("Attendu :", M + 4, y);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...COLORS.ink);
      const lines = doc.splitTextToSize(p.expected_what, W - 2 * M - 20);
      doc.text(lines, M + 20, y);
      y += lines.length * 3.6 + 1.5;
    }
    if (p.current_state) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(...COLORS.soft);
      doc.text("Observé :", M + 4, y);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...COLORS.ink);
      const lines = doc.splitTextToSize(p.current_state, W - 2 * M - 20);
      doc.text(lines, M + 20, y);
      y += lines.length * 3.6 + 1.5;
    }
    if (p.suggested_action) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(...COLORS.green);
      doc.text("Action :", M + 4, y);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...COLORS.ink);
      const lines = doc.splitTextToSize(p.suggested_action, W - 2 * M - 20);
      doc.text(lines, M + 20, y);
      y += lines.length * 3.6 + 1.5;
    }
    if (p.cost_inaction_meur_monthly > 0) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7.5);
      doc.setTextColor(...COLORS.red);
      doc.text(`Coût inaction : ${Number(p.cost_inaction_meur_monthly).toFixed(2)} M€/mois`, M + 4, y);
      y += 3.5;
    }
    y += 3;
  }
}

// -----------------------------------------------------------------------------
// Dernière page — Annexe / méthodologie
// -----------------------------------------------------------------------------

function drawAnnexPage(doc, ctx, data) {
  let y = newPage(doc, ctx);
  y = pageHeading(doc, y, "Méthodologie & annexe");

  const methodo = [
    "Position fractale : le sujet est projeté sur 5 niveaux causals (Micro > Meso > Module > Macro > Global). Le niveau où la valeur est captée détermine le levier stratégique.",
    "Relations horizontales : agrégat de l'Annuaire, de l'Utility Mapping et des chaînes de valeur. Un sujet sans relations = angle mort relationnel.",
    "Frictions & plans : croisement des frictions détectées et des plans d'action associés. Une friction sans plan devient un coût récurrent.",
    "Fantômes : NOAM interroge le silence — pour chaque type d'attente doctrinale (ressource pivot, gouvernance, réseau, timing), il détecte ce qui devrait exister et n'existe pas.",
  ];
  for (const t of methodo) {
    y = ensureSpace(doc, ctx, y, 12);
    y = bodyText(doc, y, `•  ${t}`, { size: 9, color: COLORS.ink, x: M });
    y += 2;
  }

  y += 6;
  doc.setDrawColor(...COLORS.line);
  doc.setLineWidth(0.3);
  doc.line(M, y, W - M, y);
  y += 6;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.mute);
  doc.text("MÉTADONNÉES", M, y);
  y += 5;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...COLORS.ink);
  const meta = [
    `Sujet : ${data.subject?.label || "—"}`,
    `Type : ${data.subject_type || data.subject?.type || "—"}`,
    `ID interne : ${data.subject?.id || "—"}`,
    data.doctrine && `Doctrine : ${data.doctrine.title} v${data.doctrine.version}`,
    ctx.clientName && `Préparé pour : ${ctx.clientName}`,
    `Édité le : ${fmtDateTime(new Date().toISOString())} par Noam`,
  ].filter(Boolean);
  for (const line of meta) {
    y = ensureSpace(doc, ctx, y, 6);
    doc.text(line, M, y);
    y += 4.5;
  }
}

// -----------------------------------------------------------------------------
// Orchestration
// -----------------------------------------------------------------------------

/**
 * Génère et télécharge le PDF cartographie complet.
 *
 * @param {object} data       — payload retourné par /workspace/cartographie/{type}/{id}
 * @param {object} opts
 * @param {string} opts.clientName — nom du client (« Préparé pour »)
 * @param {string} opts.filename   — base du nom de fichier
 */
export function generateCartographyPdf(data, { clientName = "", filename = "noam-cartographie" } = {}) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const ctx = {
    clientName: (clientName || "").trim(),
    subjectLabel: data?.subject?.label || "",
    pageCount: 1, // couverture = page 1
  };

  // Page 1 — Couverture (sans header/footer classique)
  drawCoverPage(doc, ctx, data);
  // Le pied de page de la couverture est intégré dans drawCoverPage

  // Pages suivantes
  drawExecutivePage(doc, ctx, data);
  if (data.fractal_position) drawFractalPage(doc, ctx, data);
  if (data.relations) drawRelationsPage(doc, ctx, data);
  drawFrictionsPage(doc, ctx, data);
  drawPlansPage(doc, ctx, data);
  if (data.phantom_scan) drawPhantomsPage(doc, ctx, data);
  drawAnnexPage(doc, ctx, data);

  // Post-pass : injecte le footer + numéros de page sur toutes les pages
  // sauf la couverture (déjà brandée à part)
  const total = doc.internal.getNumberOfPages();
  for (let i = 2; i <= total; i++) {
    doc.setPage(i);
    drawFooter(doc, ctx, i, total);
  }

  const slug = slugify(ctx.clientName);
  doc.save(`${filename}${slug ? `-${slug}` : ""}-complet.pdf`);
}
